import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from '@reach/router';
import styled from '@emotion/styled';
import { graphql, useStaticQuery, navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import { rem } from '../../../utilities/style';
import { media } from '../../../theme/media';
import { dotPatternStyles } from '../../../styles/backgrounds';
import { containerStyles } from '../../../styles/layout';
import { removeLanguageFromPath } from '../../../utilities/url';

import AnchorElement from '../../AnchorElement';
import PricingTabsCard from './PricingTabsCard';
import FeaturesTableBlock from '../FeaturesTableBlock';
import PricingTabMenu from './PricingTabMenu';
import useUserContext from '../../../hooks/use-user-context';
import Background from '../../Background';
import { createOptionMap } from '../../../utilities/misc';
import MiniCta from '../MiniCta';

const StyledBackground = styled(Background)`
  padding: ${rem(32)} 0;

  ${media('>tiny')} {
    padding: ${rem(56)} 0;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  ${containerStyles.regular}

  ${({ backgroundPattern }) => !backgroundPattern && css`
    &::before,
    &::after {
      ${dotPatternStyles}
      content: '';
      position: absolute;
      z-index: -1;
    }

    &::before {
      top: ${rem(-6)};
      left: ${rem(-4)};
      width: ${rem(180)};
      height: ${rem(112)};
    }

    &::after {
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: ${rem(360)};
      height: ${rem(112)};
    }
  `}
`;

const PricingCardContainer = styled.div`
  display: grid;
  gap: ${rem(40)};
  grid-template-columns: repeat( auto-fit, minmax(280px, 1fr) );
  height: 0;
  opacity: 0;
  visibility: hidden;
  transition: opacity 200ms ease-in, visibility 200ms ease-in;

  ${({ show }) => show && `
    height: auto;
    opacity: 1;
    visibility: visible;
  `}
`;

const tabNameMap = {
  SOS: 'sos',
  Other: 'other',
  'Business Access': 'business-access',
  'Unified Remote Support': 'remote-support'
};

const PricingTabs = ({
  displayTabs,
  showFeaturesTable,
  backgroundColor,
  backgroundPattern,
  showMobileDropdown = false,
  mobileDropdownLabel,
  mobileFeaturesTableDropdownLabel
}) => {
  const data = useStaticQuery(graphql`
    query PricingTabs {
      allContentfulOptionSet(filter: {name: {eq: "pricing_tabs"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language, developerRegions } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const [activeIndex, setActiveIndex] = useState(0);
  const { hash, pathname, search } = useLocation();
  const isSingleTab = displayTabs !== 'All';
  const singleTabName = isSingleTab ? tabNameMap[displayTabs] : null;

  // Track what page the pricing cards are shown on
  const pathWithoutLanguage = removeLanguageFromPath(pathname);
  const isPricingPage = pathWithoutLanguage.startsWith('/pricing');

  // Get the default pricing data
  let pricingDataOption = options['pricing_tabs.data'];

  // For adding FAQs below the feature table

  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (typeof window.removeAbTest === 'function' && typeof window.addAbTest === 'function') {
      if (activeIndex === 0) {
        window.removeAbTest('pricing-sos-features');
        window.removeAbTest('pricing-sos-faq');
        window.addAbTest('pricing-sba-features');
        window.addAbTest('pricing-sba-faq');
      } else if (activeIndex === 1) {
        window.removeAbTest('pricing-sba-features');
        window.removeAbTest('pricing-sba-faq');
        window.addAbTest('pricing-sos-features');
        window.addAbTest('pricing-sos-faq');
      } else {
        window.removeAbTest('pricing-sba-features');
        window.removeAbTest('pricing-sos-features');
        window.removeAbTest('pricing-sba-faq');
        window.removeAbTest('pricing-sos-faq');
      }
    }

    if (isFirstRender) {
      setIsFirstRender(false);
    }
  }, [activeIndex, isFirstRender]);

  // In the Americas only
  if (developerRegions.includes('americas')) {
    // Merge in overrides from pricing_tabs.data_americas
    pricingDataOption = {
      ...options['pricing_tabs.data'],
      ...options['pricing_tabs.data_americas']
    };
  }

  // In the EU only
  if (developerRegions.includes('eu') && pricingDataOption['business-access'].cards[0]) {
    // Merge in overrides from pricing_tabs.data_sba_solo.eu
    pricingDataOption['business-access'].cards[0] = options['pricing_tabs.data_sba_solo_eu'];

    // Remove any other pill if this card has a pill.
    const { cards } = pricingDataOption['business-access'];
    let firstNonNullPillIndex = -1; // -1 indicating no non-null pill found yet

    // Find the first card with a non-empty pill value
    for (let i = 0; i < cards.length; i++) {
      if (cards[i].pill) {
        firstNonNullPillIndex = i; // Store the index of the first card with a non-null pill
        break; // Stop the loop once the first non-null pill is found
      }
    }
    if (firstNonNullPillIndex !== -1) {
      // If a card with a non-null pill was found, set the pill of all following cards to null
      for (let i = firstNonNullPillIndex + 1; i < cards.length; i++) {
        cards[i].pill = null;
      }
    }
  }

  if (!isPricingPage) {
    // If it isn't a pricing page, then replace the 'pricing-tab' URL query with 'enterprise-cards-'
    let pricingDataJSON = JSON.stringify(pricingDataOption);
    pricingDataJSON = pricingDataJSON.replace(/from=pricing-tab-/g, 'from=enterprise-cards-');
    pricingDataOption = JSON.parse(pricingDataJSON);

    // If it isn't a pricing page & it is in the Others tab
    if (singleTabName === 'other') {
      // Merge in overrides from pricing_tabs.data_srs
      pricingDataOption = {
        ...options['pricing_tabs.data'],
        ...options['pricing_tabs.data_srs']
      };
    }
    // Not the pricing page and is SOS/Remote Support Tab
    if (singleTabName === 'remote-support') {
      // Merge in overrides from pricing_tabs.data_sos
      pricingDataOption = {
        ...options['pricing_tabs.data'],
        ...options['pricing_tabs.data_sos']
      };
    }
  }

  const pricingData = Object.entries(pricingDataOption)
    .sort((a, b) => a[1].displayOrder - b[1].displayOrder);
  const tabNames = pricingData.map(([, value]) => value.title);
  const pricingDataKeys = pricingData.map(([key]) => key);
  const ariaControlIds = pricingDataKeys.map((key, index) => (
    `pricing-tabs-tab-${key}-${index}`
  ));
  const anchorIds = pricingData.map(([key]) => key);
  const overviewRefs = useRef(new Array(pricingData.length).fill().map(() => new Set()));
  const [tallestOverviews, setTallestOverviews] = useState([]);

  const miniCtaHeadlines = pricingData.map(([, value]) => value.miniCtaHeadline);
  const miniCtaSubheads = pricingData.map(([, value]) => value.miniCtaSubhead);
  const miniCtaTexts = pricingData.map(([, value]) => value.miniCtaText);
  const miniCtaLinks = pricingData.map(([, value]) => value.miniCtaLink);

  let hasMiniCta = false;
  if (miniCtaHeadlines[activeIndex]) {
    hasMiniCta = true;
  }

  const changeTab = (index) => {
    navigate(
      `${pathname}${search}#${anchorIds[index]}`,
      { state: { disableScroll: true } }
    );
  };

  useEffect(() => {
    const hashIndex = anchorIds.indexOf(hash.replace('#', ''));
    if (hashIndex !== -1) {
      setActiveIndex(hashIndex);
    }
  }, [hash, anchorIds]);

  useEffect(() => {
    document.fonts.ready.then(() => {
      overviewRefs.current.forEach((tab, i) => {
        let tallest = 0;
        tab.forEach((overview) => {
          const overviewHeight = overview.offsetHeight;
          if (overviewHeight > tallest) {
            tallest = overviewHeight;
          }
        });
        setTallestOverviews((prevState) => {
          const newState = [...prevState];
          newState[i] = tallest;
          return newState;
        });
      });
    });
  }, []);

  // Show/Hide conditions for Pricing Cards
  // const hideCountry = ['cn', 'tw'];
  const indiaRegion = developerRegions.includes('india');
  // const hideRegion = (hideCountry.includes(country) || indiaRegion);
  // Hide SRS for CN/TW/IN
  // const isHideSRS = (card) => hideRegion && card?.devName === '{{{SRS}}}';

  // Hide SOS+10 for India
  // const isHideSOS10 = (card) => indiaRegion && card?.devName === '{{{SOS+10}}}';

  // India has special pill for SBA Solo/SOS
  const isHideSolo = (card) => indiaRegion
    && (card?.devName === '{{{SBA Solo}}}');

  // Show/Hide conditions for SRS MiniCTA
  // const miniCtaShow = pricingData.some(([, value]) => !hideRegion
  //   && (value?.devName === '{{{SRS CTA}}}'));

  return (
    <>
      {anchorIds.length > 1 && anchorIds.map((anchorId) => (
        <AnchorElement key={anchorId} id={anchorId} />
      ))}
      <StyledBackground
        color={backgroundColor}
        pattern={backgroundPattern}
        patternVariation={null}
      >
        <ContentWrapper backgroundPattern={backgroundPattern}>
          {!isSingleTab ? (
            <>
              <PricingTabMenu
                tabNames={tabNames}
                activeIndex={activeIndex}
                onChange={changeTab}
                ariaControlIds={ariaControlIds}
                showMobileDropdown={showMobileDropdown}
                mobileDropdownLabel={mobileDropdownLabel}
              />
              {pricingData.map(([key, value], i) => {
                const siblingHasPill = value.cards.some((card) => card?.pill);
                return (
                  <PricingCardContainer
                    key={key}
                    show={i === activeIndex}
                    id={ariaControlIds[i]}
                  >
                    {value.cards.map((card, j) => {
                      const customPill = isHideSolo(card) ? card?.indiaPill : card?.pill;
                      return (
                        <PricingTabsCard
                          // eslint-disable-next-line react/no-array-index-key
                          key={j}
                          pill={customPill}
                          headline={card?.headline}
                          text={card?.text}
                          priceOptionsHeadline={card?.priceOptionsHeadline}
                          textBeforePrice={card?.textBeforePrice}
                          price={card?.price}
                          priceAfterText={card?.priceAfterText}
                          priceCrossedOut={card?.priceCrossedOut}
                          priceBold={card?.priceBold}
                          duration={card?.duration}
                          durationToggle={card?.durationToggle}
                          durationToggleName={card?.durationToggleName}
                          durationToggleOptions={card?.durationToggleOptions}
                          showPriceOptionBeforePrice={card?.showPriceOptionBeforePrice}
                          priceOptionsAfterText={card?.priceOptionsAfterText}
                          buttonText={card?.buttonText}
                          buttonLink={card?.link}
                          secondaryButtonText={card?.secondaryButtonText}
                          secondaryLink={card?.secondaryLink}
                          content={card?.content}
                          tabIndex={i}
                          overviewHeight={tallestOverviews[i]}
                          ref={overviewRefs}
                          siblingHasPill={siblingHasPill}
                          numCards={value?.cards?.length}
                        />
                      );
                    })}
                  </PricingCardContainer>
                );
              })}
            </>
          ) : (
            <PricingCardContainer show>
              {(() => {
                const cards = pricingDataOption[singleTabName]?.cards || []; // Extract cards
                const siblingHasPill = cards?.some((card) => card?.pill);

                return cards?.map((card, j) => (
                  <PricingTabsCard
                    // eslint-disable-next-line react/no-array-index-key
                    key={j}
                    pill={card?.pill}
                    headline={card?.headline}
                    text={card?.text}
                    priceOptionsHeadline={card?.priceOptionsHeadline}
                    textBeforePrice={card?.textBeforePrice}
                    price={card?.price}
                    priceAfterText={card?.priceAfterText}
                    priceCrossedOut={card?.priceCrossedOut}
                    priceBold={card?.priceBold}
                    duration={card?.duration}
                    durationToggle={card?.durationToggle}
                    durationToggleName={card?.durationToggleName}
                    durationToggleOptions={card?.durationToggleOptions}
                    showPriceOptionBeforePrice={card?.showPriceOptionBeforePrice}
                    priceOptionsAfterText={card?.priceOptionsAfterText}
                    buttonText={card?.buttonText}
                    buttonLink={card?.link}
                    secondaryButtonText={card?.secondaryButtonText}
                    secondaryLink={card?.secondaryLink}
                    content={card?.content}
                    tabIndex={0}
                    overviewHeight={tallestOverviews[0]}
                    ref={overviewRefs}
                    siblingHasPill={siblingHasPill}
                    numCards={cards?.length}
                  />
                ));
              })()}
            </PricingCardContainer>
          )}
        </ContentWrapper>
      </StyledBackground>
      {/* Note: activeIndex is displayOrder - 1 from the pricing JSON */}
      {hasMiniCta
        && (
          <MiniCta
            headline={miniCtaHeadlines[activeIndex]}
            subhead={miniCtaSubheads[activeIndex]}
            primaryCtaText={miniCtaTexts[activeIndex]}
            primaryCtaLink={miniCtaLinks[activeIndex]}
          />
        )}
      {/* Features table anchor for links like "Compare Plans" */}
      {showFeaturesTable && (
        <>
          <div id="features-table" />
          <FeaturesTableBlock
            variation={isSingleTab ? singleTabName : pricingDataKeys[activeIndex]}
            showMobileDropdown={showMobileDropdown}
            mobileDropdownLabel={mobileFeaturesTableDropdownLabel}
          />
        </>
      )}
    </>
  );
};

PricingTabs.propTypes = {
  displayTabs: PropTypes.oneOf([
    'All',
    'Business Access',
    'SOS',
    'Other'
  ]).isRequired,
  showFeaturesTable: PropTypes.bool,
  backgroundColor: PropTypes.oneOf(['White', 'Light Blue']),
  backgroundPattern: PropTypes.oneOf(['Splash Dots', 'Large Dots']),
  showMobileDropdown: PropTypes.bool,
  mobileDropdownLabel: PropTypes.string,
  mobileFeaturesTableDropdownLabel: PropTypes.string
};

export const query = graphql`
  fragment PricingTabsFragment on ContentfulPricingTabs {
    id
    __typename
    displayTabs
    showFeaturesTable
    backgroundColor
    backgroundPattern
    showMobileDropdown
    mobileDropdownLabel
    mobileFeaturesTableDropdownLabel
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default PricingTabs;
